// HomePage.js
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import TabComponent from "../components/Tab";
import News from "../components/News";
import NewsArticlesSection from "../components/NewsArticlesSection";
import { Link } from "react-router-dom";
import CollegeCarouselSection from "../components/CollegeCarouselSection";
import Testimonials from "../components/Testimonials";
import YoutubeEmbed from "../components/Video";
import tabsData from "../jsonData/tabsData.json";
import TopCollege from "../components/TopCollege";
import CarouselBanner from "../components/CarouselBanner";
import SearchCollege from "../components/SearchCollege";
import useMediaQuery from "../utility/useMediaQuery";
import Carousel from "../components/ui/Carousel";
import Cookies from "js-cookie";
import apiUrl from "../utility/apiUrl";
import { preloadHomeCollegeLists } from "../utility/homeCollegeCache";
import Reveal, { RevealGroup, revealItem } from "../components/ui/Reveal";
import {
  GraduationCap,
  Search,
  BookOpen,
  BadgeCheck,
  Users,
  FileText,
  LineChart,
  Calculator,
  Calendar,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Briefcase,
  Scale,
  Palette,
  Sprout,
  Compass,
  Lightbulb,
  HeartHandshake,
  ClipboardList,
  UserCheck,
  FileCheck,
} from "lucide-react";

const CATEGORY_TILES = [
  { label: "Engineering", icon: GraduationCap },
  { label: "Medical", icon: Stethoscope },
  { label: "MBA", icon: Briefcase },
  { label: "Pharmacy", icon: BadgeCheck },
  { label: "Law", icon: Scale },
  { label: "Arts & Science", icon: Palette },
  { label: "Agriculture", icon: Sprout },
  { label: "Diploma", icon: BookOpen },
];

const TRUST_BADGES = [
  { icon: Compass, title: "Verified Information", subtitle: "Updated by our team" },
  { icon: HeartHandshake, title: "Admission Assistance", subtitle: "From enquiry to admission" },
  { icon: Lightbulb, title: "Smart Recommendations", subtitle: "Find colleges that fit you" },
  { icon: ShieldCheck, title: "Trusted & Transparent", subtitle: "Student-first approach" },
];

const HOW_IT_WORKS = [
  { step: 1, icon: Search, title: "Search & Explore", description: "Find colleges and courses as per your interest" },
  { step: 2, icon: ClipboardList, title: "Compare & Check Eligibility", description: "Use filters and compare colleges side by side" },
  { step: 3, icon: UserCheck, title: "Get Admission Assistance", description: "Our team helps with documentation & college coordination" },
  { step: 4, icon: FileCheck, title: "Get Admitted", description: "Track your application and admission status" },
];

// Tailwind's scanner needs whole, literal class names in the source — a template
// literal like `bg-${color}-50` won't be picked up, so each color maps to a
// pre-written class pair instead of being interpolated at render time.
const COLOR_CLASSES = {
  blue: { bg: "bg-blue-50", text: "text-blue-600" },
  purple: { bg: "bg-purple-50", text: "text-purple-600" },
  green: { bg: "bg-green-50", text: "text-green-600" },
  orange: { bg: "bg-orange-50", text: "text-orange-600" },
  red: { bg: "bg-red-50", text: "text-red-600" },
  indigo: { bg: "bg-indigo-50", text: "text-indigo-600" },
  pink: { bg: "bg-pink-50", text: "text-pink-600" },
  teal: { bg: "bg-teal-50", text: "text-teal-600" },
};

const FEATURES = [
  {
    icon: GraduationCap,
    color: "blue",
    title: "College Finder",
    description: "Find the perfect college based on your preferences, budget, and career goals",
  },
  {
    icon: LineChart,
    color: "purple",
    title: "Course Details",
    description: "Comprehensive information about courses, eligibility, and career prospects",
  },
  {
    icon: BadgeCheck,
    color: "green",
    title: "Scholarship Info",
    description: "Discover scholarships and financial aid opportunities for your education",
  },
  {
    icon: Users,
    color: "orange",
    title: "Admission Guide",
    description: "Step-by-step guidance for college admissions and entrance exams",
  },
  {
    icon: FileText,
    color: "red",
    title: "Exam Preparation",
    description: "Resources and tips for competitive exams like JEE, NEET, CAT, and more",
  },
  {
    icon: BookOpen,
    color: "indigo",
    title: "Career Counseling",
    description: "Expert advice to help you choose the right career path",
  },
  {
    icon: Calculator,
    color: "pink",
    title: "Fee Calculator",
    description: "Calculate total education costs including tuition, hostel, and other expenses",
  },
  {
    icon: Calendar,
    color: "teal",
    title: "Important Dates",
    description: "Stay updated with admission deadlines and exam schedules",
  },
];

const FeatureCard = ({ feature, onClick }) => {
  const Icon = feature.icon;
  const colorClasses = COLOR_CLASSES[feature.color] || COLOR_CLASSES.blue;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open WhatsApp chat for ${feature.title}`}
      className="w-full text-left bg-white rounded-2xl border border-gray-200 p-6 shadow-soft hover:shadow-card hover:-translate-y-0.5 transition-all duration-200"
    >
      <div className={`w-14 h-14 rounded-2xl ${colorClasses.bg} flex items-center justify-center mb-5`}>
        <Icon className={`h-7 w-7 ${colorClasses.text}`} />
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
      <p className="text-sm text-gray-600">{feature.description}</p>
    </button>
  );
};

const TRENDING_TABS = ["Top Colleges", "Top Courses", "Latest Exams", "Admission Updates"];

// "Top Colleges" reuses the real TopCollege carousel/data. The other three tabs
// don't have a backing feed yet (no courses/exams/updates endpoint exists) —
// show an honest "coming soon" placeholder instead of fabricating data.
const TrendingNow = () => {
  const [activeTab, setActiveTab] = React.useState(TRENDING_TABS[0]);

  return (
    <Reveal as="section" className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <LineChart className="h-5 w-5 text-orange-600" />
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">Trending Now</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {TRENDING_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={
                "px-3 py-1.5 rounded-full text-xs font-semibold transition " +
                (activeTab === tab
                  ? "bg-brand-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200")
              }
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {activeTab === "Top Colleges" ? (
        <TopCollege />
      ) : (
        <div className="py-10 text-center text-sm text-gray-500">
          {activeTab} is on the way — check back soon.
        </div>
      )}
    </Reveal>
  );
};

const Home = () => {
  const isMobile = useMediaQuery("(max-width: 767px)");

  useEffect(() => {
    const authToken = Cookies.get("token");
    const headers = {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "any",
    };
    if (authToken) headers.Authorization = String(authToken);
    preloadHomeCollegeLists({ apiUrl, headers });
  }, []);

  const openWhatsAppChat = () => {
    window.open("https://wa.me/919884702223", "_blank", "noopener,noreferrer");
  };

  return (
    <div className="w-full">
      {/* Hero — real campus photo background (Pexels, free-to-use license, no
          attribution required: photographer Kiran Pokuri Photography) with a
          brand-tinted dark overlay so the white text stays readable. */}
      <section className="relative overflow-hidden">
        {/* Below `sm`, the search form stacks into 5 rows and pushes the
            section height (~980px) way past this wide 3:2 photo's natural
            aspect ratio. Plain bg-cover/bg-center then has zero vertical
            overflow to pan — it fits the *entire* image height (building
            roof to grass) into the section, which puts the students' faces
            right behind the opaque white search card, leaving only sky/
            architecture visible above it. Zooming in and anchoring to the
            bottom on mobile only brings the students into the visible band;
            tablet/desktop already frame them fine with plain cover/center. */}
        <div
          className="absolute inset-0 bg-[length:auto_132%] bg-[position:center_bottom] sm:bg-cover sm:bg-center"
          style={{ backgroundImage: "url(/images/hero-campus.jpg)" }}
          aria-hidden="true"
        />

        {/* Brand-tinted overlay — kept VERY light: a heavy tint (it used to be 55/40/30%)
            washes the whole photo into a foggy green haze, which reads as "blurry" even
            though no blur is applied anywhere. Text contrast comes from the headline's own
            text-shadow plus the bottom scrim below (for the stats row), not from darkening
            the whole image. The photo file itself is 1920px wide, so on phones/retina it is
            still enlarged - a larger source image is the only way to make it truly sharper. */}
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand-900/30 via-brand-800/15 to-brand-700/10"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent"
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 md:pt-24 md:pb-28">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 text-white text-xs font-semibold px-3 py-1 mb-5 backdrop-blur-sm">
              <Sparkles className="h-3.5 w-3.5" />
              India's college discovery platform
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight [text-shadow:0_2px_16px_rgba(0,0,0,0.55)]">
              Find Your Dream College, the Smarter Way
            </h1>
            <p className="mt-4 text-base md:text-lg text-white/95 [text-shadow:0_1px_10px_rgba(0,0,0,0.6)]">
              Compare fees, rankings, placements and reviews across thousands of colleges
              in India — all in one place.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
            className="mt-8 max-w-2xl mx-auto bg-white rounded-2xl shadow-elevated p-3"
          >
            <SearchCollege showFilters />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
            className="mt-10 grid grid-cols-3 gap-4 max-w-xl mx-auto text-center"
          >
            {[
              { label: "Colleges listed", value: "5,000+" },
              { label: "Courses covered", value: "500+" },
              { label: "Students guided", value: "50,000+" },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-xl md:text-2xl font-extrabold text-white">{stat.value}</div>
                <div className="text-xs md:text-sm text-brand-100">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <RevealGroup className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {TRUST_BADGES.map((badge) => (
              <motion.div key={badge.title} variants={revealItem} className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-brand-50 flex items-center justify-center shrink-0">
                  <badge.icon className="h-5 w-5 text-brand-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">{badge.title}</div>
                  <div className="text-xs text-gray-500">{badge.subtitle}</div>
                </div>
              </motion.div>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* Category tiles */}
      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <RevealGroup className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {CATEGORY_TILES.map((tile) => (
              <motion.div key={tile.label} variants={revealItem}>
                <Link
                  to={`/search?keyword=${encodeURIComponent(tile.label)}`}
                  className="flex flex-col items-center gap-2 rounded-2xl bg-white border border-gray-200 p-4 text-center hover:shadow-card hover:-translate-y-0.5 transition-all no-underline"
                >
                  <tile.icon className="h-6 w-6 text-brand-600" />
                  <span className="text-xs font-semibold text-gray-800">{tile.label}</span>
                </Link>
              </motion.div>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        <TrendingNow />

        <Reveal
          as="section"
          className="rounded-2xl bg-gradient-to-br from-brand-800 to-brand-600 p-6 md:p-8 flex flex-col md:flex-row items-center gap-6 justify-between text-white"
        >
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
              <HeartHandshake className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold">Get Admission Assistance</h2>
              <p className="text-sm text-brand-100">
                Personal counsellor • College coordination • Documentation | Application support
              </p>
            </div>
          </div>
          <Link
            to="/admission-assistance"
            className="inline-flex items-center justify-center rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-brand-800 hover:bg-brand-50 no-underline whitespace-nowrap"
          >
            Apply for Admission Assistance
          </Link>
        </Reveal>

        <Reveal as="section" className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">How BharatVidyarthi Works</h2>
            <p className="text-sm text-gray-600">From search to admission, in four steps</p>
          </div>
          <RevealGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW_IT_WORKS.map((item) => (
              <motion.div key={item.step} variants={revealItem} className="text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-brand-50 flex items-center justify-center mb-3">
                  <span className="text-lg font-extrabold text-brand-700">{item.step}</span>
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-xs text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </RevealGroup>
        </Reveal>

        <Reveal as="section" className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 md:px-6 pt-4 pb-2">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">Find Your Dream College</h2>
            <p className="text-sm text-gray-600">Explore campuses across India</p>
          </div>
          <CarouselBanner />
        </Reveal>

        <Reveal as="section" className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
          <div className="mb-4">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">Private Colleges</h2>
            <p className="text-sm text-gray-600">Discover private institutions</p>
          </div>
          <CollegeCarouselSection cacheKey="private" endpoint="/api/v1/college/private/list?limit=48" />
        </Reveal>

        <Reveal as="section" className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
          <div className="mb-4">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">Featured</h2>
            <p className="text-sm text-gray-600">Popular choices right now</p>
          </div>
          <CollegeCarouselSection cacheKey="featured" endpoint="/api/v1/college/featured/list?limit=48" />
        </Reveal>

        <Reveal as="section" className="bg-white rounded-2xl border border-gray-200 pt-2 pb-4 px-4 md:pt-3 md:pb-6 md:px-6 !mt-4">
          <div className="flex items-center gap-2 mb-2 text-gray-700">
            <Search className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium">Search colleges</span>
          </div>
          <SearchCollege showFilters />
        </Reveal>

        <section className="py-4">
          <Reveal className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Everything You Need for College Admission
            </h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">
              Comprehensive tools and resources to guide you through your college
              journey
            </p>
          </Reveal>

          {isMobile ? (
            <Carousel showDots>
              {FEATURES.map((feature) => (
                <FeatureCard key={feature.title} feature={feature} onClick={openWhatsAppChat} />
              ))}
            </Carousel>
          ) : (
            <RevealGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {FEATURES.map((feature) => (
                <motion.div key={feature.title} variants={revealItem}>
                  <FeatureCard feature={feature} onClick={openWhatsAppChat} />
                </motion.div>
              ))}
            </RevealGroup>
          )}
        </section>

        <Reveal as="section" className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
          <div className="flex items-end justify-between gap-4 mb-4">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">Explore</h2>
              <p className="text-sm text-gray-600">Browse categories and filters</p>
            </div>
          </div>
          <TabComponent tabData={tabsData.tabData} carouselOnMobile />
        </Reveal>

        <Reveal as="section" className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
          <div className="mb-4">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">Latest News</h2>
            <p className="text-sm text-gray-600">Updates from colleges and education</p>
          </div>
          <News numberofCards={8} layout="grid4" />
        </Reveal>

        <Reveal as="section" className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">News Articles</h2>
              <p className="text-sm text-gray-600">
                Admission guides and expert insights on colleges, courses, and exams
              </p>
            </div>
            <Link
              to="/articles"
              className="text-sm font-semibold text-brand-600 hover:text-brand-700 no-underline whitespace-nowrap"
            >
              View All
            </Link>
          </div>
          <NewsArticlesSection numberofCards={4} />
        </Reveal>

        <Reveal as="section" className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
          <div className="mb-4">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">Videos</h2>
            <p className="text-sm text-gray-600">Watch campus and guidance content</p>
          </div>
          <YoutubeEmbed />
        </Reveal>

        <Reveal as="section" className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
          <Testimonials />
        </Reveal>

        <Reveal
          as="section"
          className="rounded-2xl border border-gray-200 bg-gray-50 p-6 md:p-8 flex flex-col sm:flex-row items-center gap-4 justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-none">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
            </div>
            <p className="text-sm text-gray-700">
              Verified college data, updated regularly — trusted by students across India.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default Home;
